/** biome-ignore-all lint/correctness/useUniqueElementIds: Necessary for playwright tests */
import cn from 'classnames';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFeatureUser } from 'sonata-react-components';
import { checkUserResourcePermissions, checkUserRouteAccess } from 'sonata-static-storage/lib/helpers/user.helpers';

import { type MainOptionId, useMenu } from '../Menu.context';

import type { MouseEvent } from 'react';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';

import './mainMenu.scss';

import React from 'react';

import { MenuDropdown, type MenuDropdownItem } from '../MenuDropdown';

type Props = {
  user: UserInterface;
  userRoles: UserRoleInterface[];
  guardHardNavigation?: (event: MouseEvent, action: () => void) => void;
  isShared?: boolean;
};

type MainMenuOption = {
  id: MainOptionId;
  isDisabled?: boolean;
  onClick: () => void;
  permissions: string[];
  text: string;
  items?: MenuDropdownItem & { permission: string }[];
};

export function MainMenu({ guardHardNavigation, user, userRoles, isShared }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    setMenuItemSelected,
    state: { mainItemSelected }
  } = useMenu();
  const hasDeviatedLI = useFeatureUser('deviatedLI');

  // Shared report sessions see the logo only, with no link to the root path.
  if (isShared) {
    return (
      <div className="mainMenu">
        <span className="mainMenu__logo">
          <h1 id="sonataLogo">Sonata</h1>
        </span>
      </div>
    );
  }

  const menuOptions = [
    {
      id: 'campaigns' as MainOptionId,
      onClick: () => {
        navigate('/', { replace: true });
      },
      permissions: ['v2'],
      text: formatMessage({
        defaultMessage: 'My Campaigns',
        id: `menu.routes.myCampaigns`
      })
    },
    {
      id: 'pixels' as MainOptionId,
      permissions: ['myPixels'],
      text: formatMessage({
        defaultMessage: 'My Pixels',
        id: `menu.routes.pixels`
      })
    },
    {
      id: 'contextSystem' as MainOptionId,
      onClick: () => {
        navigate('/contextSystem');
      },
      permissions: ['myContextBuilder'],
      text: formatMessage({
        defaultMessage: 'Context System',
        id: `menu.routes.contextSystem`
      })
    },
    {
      id: 'insights' as MainOptionId,
      items: [
        {
          id: 'MyNewInsights',
          onClick: () => {
            navigate('/insights?type=insights');
          },
          permission: 'MyNewInsights',
          title: formatMessage({
            defaultMessage: 'Insights 2.0',
            id: 'menu.routes.insights2'
          })
        },
        {
          id: 'MyBusinessInsights',
          onClick: () => {
            navigate('/insights?type=business');
          },
          permission: 'MyBusinessInsights',
          title: formatMessage({
            defaultMessage: 'Business Insights',
            id: 'menu.routes.businessInsights'
          })
        }
      ],
      permissions: ['MyNewInsights', 'MyBusinessInsights'],
      text: formatMessage({
        defaultMessage: 'Insights',
        id: `menu.routes.insights`
      })
    },
    {
      id: 'assignDealsModal' as MainOptionId,
      items: [
        {
          icon: 'fa-regular fa-ballot-check',
          id: 'dealManager',
          onClick: () => {
            setMenuItemSelected('dealManagerModal' as MainOptionId);
          },
          permission: 'assignDealsModal',
          title: formatMessage({
            defaultMessage: 'Deal Manager',
            id: 'menu.routes.dealManager'
          })
        },
        {
          icon: 'fa-regular fa-user-plus',
          id: 'assignDeals',
          onClick: () => {
            setMenuItemSelected('assignDealsModal' as MainOptionId);
          },
          permission: 'assignDealsModal',
          title: formatMessage({
            defaultMessage: 'Assign Deals',
            id: 'menu.routes.assignDealsModal'
          })
        }
      ],
      permissions: ['assignDealsModal'],
      text: formatMessage({
        defaultMessage: 'Assign Deals',
        id: `menu.routes.assignDealsModal`
      })
    },
    ...(hasDeviatedLI
      ? [
          {
            id: 'deviatedLineItems' as MainOptionId,
            onClick: () => {
              navigate('/deviatedLineItems');
            },
            permissions: ['v2'],
            text: formatMessage({
              defaultMessage: 'Delivery Status',
              id: 'menu.routes.deviatedLineItems'
            })
          }
        ]
      : [])
  ].map(i => {
    if (i.items || i.id === 'contextSystem') {
      return i;
    }
    return {
      ...i,
      onClick: () => {
        setMenuItemSelected(i.id as MainOptionId);
        i.onClick?.();
      }
    };
  }) as MainMenuOption[];

  const checkPermission = (code: string) => {
    const entry = taptap.app.platformPermissions.find((p: Record<string, any>) => p.code === code);
    return entry?.type === 'permission'
      ? checkUserResourcePermissions(code, user, userRoles)
      : checkUserRouteAccess(code, user, userRoles);
  };

  const getComponent = (option: MainMenuOption): JSX.Element | null => {
    const hasPermission = option.permissions.some(p => checkPermission(p));

    if (!hasPermission) {
      return null;
    }
    const checkIfOptionIsActive = (option: MainMenuOption): boolean => {
      if (option.id === 'campaigns') {
        return location.pathname === '/' || location.pathname === '/campaigns';
      }
      if (option.id === 'contextSystem') {
        return location.pathname === '/contextSystem';
      }
      if (option.id === 'insights') {
        return location.pathname === '/insights';
      }
      if (option.id === 'deviatedLineItems') {
        return location.pathname === '/deviatedLineItems';
      }
      return option.id === mainItemSelected;
    };
    if (!option.items) {
      return (
        <button
          className={cn('mainMenu__item', {
            'mainMenu__item--disabled': option.isDisabled,
            'mainMenu__item--selected': checkIfOptionIsActive(option)
          })}
          key={option.id}
          type="button"
          {...(!option.isDisabled && { onClick: option.onClick })}
        >
          <span>{option.text}</span>
        </button>
      );
    }
    const items = option.items.filter(i => checkPermission(i.permission)) as unknown as MenuDropdownItem[];

    if (items.length === 0) {
      return null;
    }

    return (
      <MenuDropdown
        items={items}
        options={{ isMenuItem: true, isSelected: checkIfOptionIsActive(option) }}
        title={option.text}
      />
    );
  };

  return (
    <div className="mainMenu">
      <a
        className="mainMenu__logo"
        href="/"
        onClick={e =>
          guardHardNavigation?.(e, () => {
            window.location.href = '/';
          })
        }
        style={{ textDecoration: 'none' }}
      >
        <h1 id="sonataLogo">Sonata</h1>
      </a>
      <div className="mainMenu__items">
        {menuOptions.map(o => (
          <React.Fragment key={o.id}>{getComponent(o)}</React.Fragment>
        ))}
      </div>
    </div>
  );
}
